import axios, { type AxiosError, type AxiosInstance } from 'axios';
import axiosRetry, { type IAxiosRetryConfig } from 'axios-retry';
import { ENV_NAME, Region } from '../config';
import { AuthRetryRevisionStaleError } from '../errors';
import type { AuthRecoveryContext, ScribeHttpClientDeps } from './types';

export type TargetSpec =
  | 'v1'
  | 'v2'
  | 'v2_1'
  | 'v2_2'
  | 'identityBroker'
  | 'openApi'
  | 'linkShare'
  | { readonly customUrl: () => string | undefined; readonly initialUrl?: string };

export type AuthSpec =
  | 'session'
  | 'widget'
  | 'none'
  | {
      readonly type: 'session';
      readonly allowExplicitToken?: boolean;
      readonly emptyBehavior?: 'omit' | 'empty-string';
      readonly clientIdentityCondition?: 'always' | 'when-authenticated';
    };

export type WorkspaceSpec = 'always' | 'preserveCaller' | 'none';
export type ErrorTierSpec = 'tier1' | 'tier2' | 'none';

export interface HttpClientSpec {
  readonly target: TargetSpec;
  readonly auth?: AuthSpec;
  readonly workspace?: WorkspaceSpec;
  readonly tempUser?: boolean;
  readonly errorTier?: ErrorTierSpec;
  readonly retry?: IAxiosRetryConfig | number;
}

function resolveTargetUrls(
  deps: ScribeHttpClientDeps,
  target: TargetSpec
): { initialUrl: string; dynamicUrl: () => string | undefined } {
  const domain = deps.getDefaultScribeDomain();
  if (typeof target === 'object') {
    return {
      initialUrl: target.initialUrl ?? target.customUrl() ?? '',
      dynamicUrl: target.customUrl,
    };
  }

  switch (target) {
    case 'v1':
      return {
        initialUrl: `${domain}/api/v1/ml-scribe/`,
        dynamicUrl: () => deps.getDomains()?.baseV1Url,
      };
    case 'v2':
      return {
        initialUrl: `${domain}/api/v2/ml-scribe/`,
        dynamicUrl: () => deps.getDomains()?.baseV2Url,
      };
    case 'v2_1':
      return {
        initialUrl: `${domain}/api/v2-1/ml-scribe/`,
        dynamicUrl: () => deps.getDomains()?.baseV2_1Url,
      };
    case 'v2_2':
      return {
        initialUrl: `${domain}/api/v2-2/ml-scribe/`,
        dynamicUrl: () => deps.getDomains()?.baseV2_2Url,
      };
    case 'openApi':
      return {
        initialUrl: `${domain}/api/v2/ml-scribe/open-api/`,
        dynamicUrl: () => deps.getDomains()?.openApiBaseUrl,
      };
    case 'identityBroker': {
      const getBrokerUrl = () => {
        const region = deps.getEnvironment() === ENV_NAME.PROD ? Region.EU : Region.AU;
        return deps.getDomains(region)?.baseV1IdentityBrokerUrl;
      };
      return {
        initialUrl: `${domain}/api/v1/identity-broker/`,
        dynamicUrl: getBrokerUrl,
      };
    }
    case 'linkShare':
      return {
        initialUrl: `${domain}/api/v2/ml-scribe/sessions/shared/`,
        dynamicUrl: () => {
          const v2 = deps.getDomains()?.baseV2Url;
          return v2 ? `${v2}sessions/shared/` : undefined;
        },
      };
  }
}

async function resolveAuthContext(
  deps: ScribeHttpClientDeps,
  explicitToken?: string,
  expectedAuthIdentity?: { provider: string; authRevision: number }
): Promise<{
  token: string | null;
  context: {
    provider: string;
    authRevision: number;
    eligible: boolean;
    stale: boolean;
  } | null;
}> {
  if (explicitToken !== undefined) return { token: explicitToken, context: null };
  if (!deps.getAuthContext) {
    if (expectedAuthIdentity !== undefined) {
      throw new AuthRetryRevisionStaleError();
    }
    return { token: (await deps.getAuthToken()) ?? null, context: null };
  }
  const resolved: AuthRecoveryContext | null = await deps.getAuthContext();
  if (expectedAuthIdentity !== undefined) {
    if (
      !resolved ||
      resolved.stale ||
      resolved.provider !== expectedAuthIdentity.provider ||
      resolved.authRevision !== expectedAuthIdentity.authRevision
    ) {
      throw new AuthRetryRevisionStaleError();
    }
  }
  if (!resolved) return { token: null, context: null };
  return {
    token: resolved.token,
    context: {
      provider: resolved.provider,
      authRevision: resolved.authRevision,
      eligible: resolved.eligible,
      stale: resolved.stale,
    },
  };
}

export function computeBaseUrls(deps: ScribeHttpClientDeps) {
  const scribeDomain = deps.getDefaultScribeDomain();
  const baseURL = `${scribeDomain}/api/v1/ml-scribe/`;
  const baseV1IdentityBrokerUrl = `${scribeDomain}/api/v1/identity-broker/`;
  const baseV2URL = `${scribeDomain}/api/v2/ml-scribe/`;
  const baseV2_1URL = `${scribeDomain}/api/v2-1/ml-scribe/`;
  const baseV2_2URL = `${scribeDomain}/api/v2-2/ml-scribe/`;
  const openApiBaseUrl = `${scribeDomain}/api/v2/ml-scribe/open-api/`;
  const linkShareBaseUrl = `${scribeDomain}/api/v2/ml-scribe/sessions/shared/`;

  const getScribeBaseUrlFromV2 = () => {
    const baseV2Url = deps.getDomains()?.baseV2Url ?? baseV2URL;
    return baseV2Url.replace(/\/api\/v2\/ml-scribe\/?$/, '');
  };

  return {
    baseURL,
    baseV1IdentityBrokerUrl,
    baseV2URL,
    baseV2_1URL,
    baseV2_2URL,
    openApiBaseUrl,
    linkShareBaseUrl,
    getScribeBaseUrlFromV2,
  };
}

export function createHttpClient(
  deps: ScribeHttpClientDeps,
  spec: HttpClientSpec
): AxiosInstance {
  const { initialUrl, dynamicUrl } = resolveTargetUrls(deps, spec.target);
  const instance = axios.create({ baseURL: initialUrl });

  const authType = typeof spec.auth === 'object' ? spec.auth.type : (spec.auth ?? 'none');
  const allowExplicitToken =
    typeof spec.auth === 'object' && spec.auth.allowExplicitToken;
  const emptyBehavior = typeof spec.auth === 'object' && spec.auth.emptyBehavior;
  const identityCondition =
    typeof spec.auth === 'object' && spec.auth.clientIdentityCondition;

  instance.interceptors.request.use(async (config) => {
    const url = dynamicUrl();
    if (url) config.baseURL = url;

    let shouldApplyIdentity = true;

    if (authType === 'session') {
      const { token, context } = await resolveAuthContext(
        deps,
        allowExplicitToken ? config.explicitKindeToken : undefined,
        config.__expectedAuthIdentity
      );

      // Always replace request-auth metadata so a reused config cannot retain
      // stale values. Bearer presence is independent from identity context
      // because suppression can intentionally resolve no context.
      config.__authHadBearer = Boolean(token);
      config.__authRecoveryContext = context ?? undefined;

      if (identityCondition === 'when-authenticated') {
        shouldApplyIdentity = Boolean(token);
      }

      if (config.headers) {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else if (emptyBehavior !== 'omit') {
          config.headers.Authorization = '';
        }
      }
    } else if (authType === 'widget') {
      const token = deps.getWidgetToken();
      if (config.headers) {
        config.headers.Authorization = token ? `Bearer ${token}` : '';
      }
    }

    if (config.headers) {
      if (shouldApplyIdentity) {
        config.headers['X-Client-Id'] = deps.getClientId();
        const v = deps.getClientVersion();
        if (v) config.headers['X-Client-Version'] = v;
      }

      deps.applyTracingHeaders(config.headers);

      if (spec.workspace && spec.workspace !== 'none') {
        const wsId = deps.getWorkspaceId();
        const isPreserve = spec.workspace === 'preserveCaller';
        if (wsId && (!isPreserve || !config.headers[deps.workspaceIdHeaderName])) {
          config.headers[deps.workspaceIdHeaderName] = wsId;
        }
      }

      if (spec.tempUser) {
        const tempUserId = deps.getTempUserId();
        if (tempUserId) config.headers['X-Temp-User-ID'] = tempUserId;
      }
    }

    return config;
  });

  if (spec.errorTier && spec.errorTier !== 'none') {
    const handler =
      spec.errorTier === 'tier1'
        ? deps.onResponseError
        : (deps.onResponseErrorTier2 ?? deps.onResponseError);

    instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => handler(error, (cfg) => instance.request(cfg))
    );
  }

  if (spec.retry !== undefined) {
    const retryConfig: IAxiosRetryConfig =
      typeof spec.retry === 'number'
        ? {
            retries: spec.retry,
            retryDelay: (retryCount) => axiosRetry.exponentialDelay(retryCount),
          }
        : {
            retryDelay: (retryCount) => axiosRetry.exponentialDelay(retryCount),
            ...spec.retry,
          };

    axiosRetry(instance, retryConfig);
  }

  return instance;
}
